/**
 * Notification Center Component
 *
 * Dropdown component showing user notifications with mark as read functionality.
 */

import { useState, useCallback } from 'react';
import {
  Badge,
  Dropdown,
  List,
  Typography,
  Button,
  Space,
  Spin,
  Empty,
  Divider,
  Tag,
} from 'antd';
import {
  BellOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  UserAddOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { notificationsApi, type Notification } from '@api/index';

dayjs.extend(relativeTime);

const { Text, Paragraph } = Typography;

const notificationIcons: Record<string, React.ReactNode> = {
  reminder: <ClockCircleOutlined style={{ color: 'var(--status-info)' }} />,
  overdue: <ExclamationCircleOutlined style={{ color: 'var(--status-error)' }} />,
  assignment: <UserAddOutlined style={{ color: 'var(--status-success)' }} />,
  completed: <CheckCircleOutlined style={{ color: 'var(--status-success)' }} />,
  system: <BellOutlined style={{ color: 'var(--text-tertiary)' }} />,
};

const notificationColors: Record<string, string> = {
  reminder: 'blue',
  overdue: 'red',
  assignment: 'green',
  completed: 'green',
  system: 'default',
};

interface NotificationItemProps {
  notification: Notification;
  onClick: (notification: Notification) => void;
}

function NotificationItem({ notification, onClick }: NotificationItemProps) {
  return (
    <List.Item
      onClick={() => onClick(notification)}
      style={{
        cursor: 'pointer',
        backgroundColor: notification.isRead ? 'transparent' : '#f0f5ff',
        padding: '12px 16px',
      }}
    >
      <List.Item.Meta
        avatar={notificationIcons[notification.notificationType] || notificationIcons.system}
        title={
          <Space>
            <Text strong={!notification.isRead}>{notification.title}</Text>
            {!notification.isRead && (
              <Badge status="processing" />
            )}
          </Space>
        }
        description={
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            <Paragraph
              ellipsis={{ rows: 2 }}
              style={{ margin: 0, fontSize: 13 }}
            >
              {notification.message}
            </Paragraph>
            <Space size={4}>
              <Tag color={notificationColors[notification.notificationType]}>
                {notification.notificationType.replace('_', ' ')}
              </Tag>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {dayjs(notification.createdAt).fromNow()}
              </Text>
            </Space>
          </Space>
        }
      />
    </List.Item>
  );
}

export default function NotificationCenter() {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();

  // Fetch notifications
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => notificationsApi.list({ limit: 20 }),
    refetchInterval: 60000, // Refetch every minute
  });

  // Mark as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: (notificationIds: string[]) =>
      notificationsApi.markAsRead(notificationIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  // Mark all as read mutation
  const markAllAsReadMutation = useMutation({
    mutationFn: () => notificationsApi.markAllAsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const handleNotificationClick = useCallback(
    (notification: Notification) => {
      if (!notification.isRead) {
        markAsReadMutation.mutate([notification.id]);
      }
      // If notification has an instance, could navigate to it
      // For now just mark as read
    },
    [markAsReadMutation]
  );

  const handleMarkAllRead = useCallback(() => {
    markAllAsReadMutation.mutate();
  }, [markAllAsReadMutation]);

  const notifications = data?.notifications || [];
  const unreadCount = data?.unreadCount || 0;

  const dropdownContent = (
    <div
      style={{
        width: 400,
        maxHeight: 500,
        backgroundColor: '#fff',
        borderRadius: 8,
        boxShadow: '0 3px 6px -4px rgba(0,0,0,.12), 0 6px 16px 0 rgba(0,0,0,.08)',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 16px',
          borderBottom: '1px solid #f0f0f0',
        }}
      >
        <Space>
          <Text strong>Notifications</Text>
          {unreadCount > 0 && (
            <Badge count={unreadCount} size="small" />
          )}
        </Space>
        {unreadCount > 0 && (
          <Button
            type="link"
            size="small"
            icon={<CheckOutlined />}
            onClick={handleMarkAllRead}
            loading={markAllAsReadMutation.isPending}
          >
            Mark all read
          </Button>
        )}
      </div>

      {/* Notifications List */}
      <div style={{ maxHeight: 400, overflowY: 'auto' }}>
        {isLoading ? (
          <div style={{ padding: 40, textAlign: 'center' }}>
            <Spin />
          </div>
        ) : notifications.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No notifications"
            style={{ padding: 40 }}
          />
        ) : (
          <List
            dataSource={notifications}
            renderItem={(notification) => (
              <NotificationItem
                notification={notification}
                onClick={handleNotificationClick}
              />
            )}
          />
        )}
      </div>

      {/* Footer */}
      {notifications.length > 0 && (
        <>
          <Divider style={{ margin: 0 }} />
          <div style={{ padding: 8, textAlign: 'center' }}>
            <Button type="link" onClick={() => refetch()}>
              Refresh
            </Button>
          </div>
        </>
      )}
    </div>
  );

  return (
    <Dropdown
      popupRender={() => dropdownContent}
      trigger={['click']}
      open={open}
      onOpenChange={setOpen}
      placement="bottomRight"
    >
      <Badge count={unreadCount} size="small" offset={[-2, 2]}>
        <Button
          type="text"
          icon={<BellOutlined style={{ fontSize: 20 }} />}
          style={{ color: '#595959' }}
        />
      </Badge>
    </Dropdown>
  );
}
